/**
 * The active Apply Content Language job for the current account.
 *
 * The job outlives every component that shows it. A user starts one, switches
 * from Description to Highlights, opens another view, and comes back; the job
 * is still running and the progress must still be there. So the job lives here
 * and the polling loop is owned by the store, not by a component's lifecycle.
 * A component mounting simply reads what the store already holds.
 *
 * Three hazards shape the rest of this module:
 *
 * - Duplicate work. One poller and one start submission at a time, enforced by
 *   flags rather than by trusting a button's disabled state.
 * - Stale responses. A poll for one account can land after the user has moved
 *   to another. Every response is checked against the account and job it was
 *   issued for and dropped if either has moved on.
 * - Confusing a failed refresh with a failed job. A poll that cannot reach the
 *   service says exactly that and leaves the last known job status standing.
 *   Only the service calls a job failed.
 */
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import type { LanguageJobFailure, LanguageJobStatus, VinResult } from '../languageJob'
import { isIssueVinResult, isTerminalStatus } from '../languageJob'
import { fetchLanguageJobResults, fetchLanguageJobStatus, startLanguageJob } from '../languageJobApi'
import { useAccountSettingsStore } from './accountSettings'

export const POLL_INTERVAL_MS = 5000

export type ResultDetailsState = 'idle' | 'loading' | 'loaded' | 'failed'

interface ResultLoadOperation {
  accountId: string
  jobId: string
  generation: number
  promise: Promise<void>
}

export const useLanguageJobStore = defineStore('languageJob', () => {
  const accountSettings = useAccountSettingsStore()

  /** The account the tracked job belongs to, which is not always the current one. */
  const jobAccountId = ref('')
  const jobId = ref('')
  const job = ref<LanguageJobStatus | null>(null)

  const starting = ref(false)
  const startFailure = ref<LanguageJobFailure | null>(null)
  /** A poll that failed. Distinct from a job the service reported as failed. */
  const refreshFailure = ref<LanguageJobFailure | null>(null)

  const issueResults = ref<VinResult[]>([])
  const resultDetailsState = ref<ResultDetailsState>('idle')

  let pollTimer: ReturnType<typeof setTimeout> | null = null
  let polling = false
  let resultLoadGeneration = 0
  let resultLoadOperation: ResultLoadOperation | undefined

  const hasJob = computed(() => jobId.value !== '')
  const status = computed(() => job.value?.status ?? null)
  const isRunning = computed(() => hasJob.value && !isTerminalStatus(status.value ?? 'running'))

  /**
   * True only for a response issued for the job and account still being
   * tracked. Anything else is the tail of a request the user has already moved
   * past, and applying it would show one account's progress under another.
   */
  const isCurrent = (forAccountId: string, forJobId: string): boolean =>
    jobAccountId.value === forAccountId && jobId.value === forJobId

  const invalidateResultDetails = () => {
    resultLoadGeneration += 1
    resultLoadOperation = undefined
    issueResults.value = []
    resultDetailsState.value = 'idle'
  }

  const isCurrentResultLoad = (
    forAccountId: string,
    forJobId: string,
    generation: number,
  ): boolean =>
    isCurrent(forAccountId, forJobId) &&
    job.value?.jobId === forJobId &&
    resultLoadGeneration === generation

  const stopPolling = () => {
    if (pollTimer !== null) {
      clearTimeout(pollTimer)
      pollTimer = null
    }
    polling = false
  }

  const reset = () => {
    stopPolling()
    jobAccountId.value = ''
    jobId.value = ''
    job.value = null
    starting.value = false
    startFailure.value = null
    refreshFailure.value = null
    invalidateResultDetails()
  }

  const refresh = async (): Promise<void> => {
    const forAccountId = jobAccountId.value
    const forJobId = jobId.value
    if (!forAccountId || !forJobId) return

    const result = await fetchLanguageJobStatus(forAccountId, forJobId)
    if (!isCurrent(forAccountId, forJobId)) return

    if (!result.ok) {
      // The job is whatever it was. Only the service can call it failed.
      refreshFailure.value = result.failure
      return
    }

    refreshFailure.value = null
    job.value = result.data
  }

  /**
   * Poll until the service reports a terminal status.
   *
   * Chained timeouts rather than an interval, so a slow response cannot stack
   * requests, and a refresh failure retries on the same cadence instead of
   * abandoning a job that is probably still running.
   */
  const pollLoop = async (): Promise<void> => {
    await refresh()

    if (!polling || !jobId.value) return
    if (isTerminalStatus(status.value ?? '')) {
      stopPolling()
      return
    }

    pollTimer = setTimeout(() => {
      void pollLoop()
    }, POLL_INTERVAL_MS)
  }

  const startPolling = () => {
    if (polling || !jobId.value) return
    polling = true
    void pollLoop()
  }

  /**
   * Track a job and follow it to completion. Used both after starting one and
   * when the service reports that a job for this account is already running.
   */
  const attachToJob = (accountId: string, id: string) => {
    if (jobId.value === id && jobAccountId.value === accountId) {
      startPolling()
      return
    }
    stopPolling()
    jobAccountId.value = accountId
    jobId.value = id
    job.value = null
    refreshFailure.value = null
    invalidateResultDetails()
    startPolling()
  }

  /**
   * Start a job for the current account.
   *
   * A failed start clears itself out of the way so the action can be retried;
   * only a started or already-running job leaves the store tracking one. A
   * conflict that names the running job is not a failure to report: it is the
   * job the user wanted to see, so it is adopted.
   */
  const start = async (): Promise<boolean> => {
    if (starting.value || isRunning.value) return false

    const accountId = accountSettings.accountId
    if (!accountId) return false

    starting.value = true
    startFailure.value = null

    try {
      const result = await startLanguageJob(accountId)
      if (accountSettings.accountId !== accountId) return false

      if (result.ok) {
        attachToJob(accountId, result.data.jobId)
        return true
      }

      if (result.failure.kind === 'conflict' && result.failure.jobId) {
        attachToJob(accountId, result.failure.jobId)
        return true
      }

      startFailure.value = result.failure
      return false
    } finally {
      starting.value = false
    }
  }

  /**
   * Load every result page in order, validate the terminal snapshot as a
   * complete set, then publish only exception VINs in server order.
   *
   * The local accumulator is intentional: a failed or stale request never
   * exposes a partial table. A successful load is a same-job cache; callers
   * can safely invoke this action again when details are re-expanded.
   */
  const loadIssueResults = (): Promise<void> => {
    const forAccountId = jobAccountId.value
    const forJobId = jobId.value
    const terminalJob = job.value
    if (
      !forAccountId ||
      !forJobId ||
      !terminalJob ||
      terminalJob.jobId !== forJobId ||
      terminalJob.status !== 'completed' ||
      terminalJob.mode !== 'apply' ||
      terminalJob.totals.skipped + terminalJob.totals.errors === 0
    ) {
      return Promise.resolve()
    }

    if (resultDetailsState.value === 'loaded') return Promise.resolve()
    if (
      resultLoadOperation &&
      resultLoadOperation.accountId === forAccountId &&
      resultLoadOperation.jobId === forJobId &&
      resultLoadOperation.generation === resultLoadGeneration
    ) {
      return resultLoadOperation.promise
    }

    const expectedPageCount = terminalJob.resultPages
    const expectedVinCount = terminalJob.vinsTotal
    const expectedIssueOutcomes = terminalJob.totals.skipped + terminalJob.totals.errors
    const generation = ++resultLoadGeneration

    issueResults.value = []
    resultDetailsState.value = 'loading'

    const operation = {} as ResultLoadOperation
    const task = (async () => {
      try {
        if (!Number.isInteger(expectedPageCount) || expectedPageCount < 1) {
          throw new Error('Invalid result page count')
        }

        const allResults: VinResult[] = []
        const vins = new Set<string>()

        for (let page = 0; page < expectedPageCount; page += 1) {
          const result = await fetchLanguageJobResults(forAccountId, forJobId, page)
          if (!isCurrentResultLoad(forAccountId, forJobId, generation)) return
          if (!result.ok) throw new Error('Result page request failed')

          const response = result.data
          if (response.jobId !== forJobId) throw new Error('Result job ID mismatch')
          if (response.page !== page) throw new Error('Result page mismatch')
          if (response.pageCount !== expectedPageCount) {
            throw new Error('Result page count mismatch')
          }
          if (!Array.isArray(response.results)) throw new Error('Invalid result collection')

          for (const vinResult of response.results) {
            if (vins.has(vinResult.vin)) throw new Error('Duplicate VIN in result pages')
            vins.add(vinResult.vin)
            allResults.push(vinResult)
          }
        }

        if (!isCurrentResultLoad(forAccountId, forJobId, generation)) return
        if (allResults.length !== expectedVinCount) throw new Error('Result count mismatch')
        if (vins.size !== expectedVinCount) throw new Error('Unique VIN count mismatch')

        const filteredResults = allResults.filter(isIssueVinResult)
        if (expectedIssueOutcomes > 0 && filteredResults.length === 0) {
          throw new Error('Issue summary has no matching VIN results')
        }

        if (!isCurrentResultLoad(forAccountId, forJobId, generation)) return
        issueResults.value = filteredResults
        resultDetailsState.value = 'loaded'
      } catch {
        if (!isCurrentResultLoad(forAccountId, forJobId, generation)) return
        issueResults.value = []
        resultDetailsState.value = 'failed'
      }
    })()

    operation.accountId = forAccountId
    operation.jobId = forJobId
    operation.generation = generation
    operation.promise = task.finally(() => {
      if (resultLoadOperation === operation) resultLoadOperation = undefined
    })
    resultLoadOperation = operation
    return operation.promise
  }

  const retryIssueResults = (): Promise<void> =>
    resultDetailsState.value === 'failed' ? loadIssueResults() : Promise.resolve()

  // A job belongs to one account. Switching accounts abandons it rather than
  // letting its progress appear to describe the account now on screen.
  watch(
    () => accountSettings.accountId,
    (current) => {
      if (jobAccountId.value && current !== jobAccountId.value) reset()
    },
  )

  return {
    jobAccountId,
    jobId,
    job,
    starting,
    startFailure,
    refreshFailure,
    issueResults,
    resultDetailsState,

    hasJob,
    status,
    isRunning,

    start,
    attachToJob,
    refresh,
    startPolling,
    stopPolling,
    loadIssueResults,
    retryIssueResults,
    reset,
  }
})
